import React from 'react';
import classNames from 'classnames';
import styles from './FailureModal.module.scss';
import Button from '../Button';
import { Text } from '../typography';
import { Icon } from '@mui/material';
import { useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import MultiFileDropzone from '../image_uploads/MultiFileDropzone';
import { useInspection } from '../../hooks/offline-hooks/createInspectionhook';
import UserSelector from '../UserSelector';
import { closeFailureModal } from '../../graphql/cache/modal';
import { keys } from '../../utilities/translator/translation_keys.js';
import ImageWrapper from '../offline-wrapper-functions/image-wrapper';
import { useTranslation } from 'react-i18next';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc.js';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import TextWithIcon from '../typography/TextWithIcon.jsx';
import SimpleUserCard from '../SimpleUserCard.jsx';
import Spacer from '../typography/Spacer.jsx';
import Label from '../action_items/Label.jsx';
import { statusStringLookup } from '../action_items/action_item_utilities.js';
import { customColors } from '../../utilities/misc.js';

const existingActionItemQuery = loader('./FailureModal.actionItems.graphql');

export default function FailureModal({
  show = false,
  title,
  description,
  onSubmit,
  onCancel,
  external = false,
  questionId,
  assetId,
}) {
  const [notes, setNotes] = useState('');
  const [assignee, setAssignee] = useState();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const online = useOnlineStatus();
  const [showAssign, setShowAssign] = useState(true);
  const { addImageResults } = useInspection();

  const onHide = () => {
    setAssignee(undefined);
    closeFailureModal();
    setLoading(false);
    setImages([]);
    setNotes('');
  };
  const { t } = useTranslation();

  const handleFilesSelected = (files) => {
    setLoading(true);
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => addImageResults(null, resolve, file)),
      ),
    ).then((urls) => {
      setImages((prev) => [...prev, ...urls]);
      setLoading(false);
    });
  };

  const { data: { actionItems = [], actionItemsCount = 0 } = {} } = useQuery(
    existingActionItemQuery,
    {
      skip: !show || !questionId || !assetId,
      variables: {
        questionId: `${questionId}`,
        assetId: `${assetId}`,
      },
      onCompleted: (data) => {
        if (data.actionItemsCount >= 1) {
          setShowAssign(false);
        }
      },
    },
  );

  return show ? (
    <div className={show ? styles.show : styles.hide}>
      <div className={styles.background}>
        <div
          className={classNames(
            styles.container,
            show ? styles.showContainer : styles.hideContainer,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.contentContainer}>
            <div className={styles.header}>
              <Text weight="bold" size="lg" noMargin>
                {t(keys.assessments.WHAT_WENT_WRONG)}
              </Text>
              <Icon
                sx={{ fontSize: '1.5rem' }}
                fontSize="inherit"
                className={styles.closeIcon}
                baseClassName="material-icons-outlined"
                onClick={() => onHide()}
              >
                close
              </Icon>
            </div>
            <br />
            <div>
              <div className={styles.titleContainer}>
                <Text noMargin weight="bold" color="red" size="lg">
                  {title}
                </Text>
                <Icon
                  sx={{ fontSize: '2rem' }}
                  className={styles.icon}
                  fontSize="inherit"
                  baseClassName="material-icons-outlined"
                >
                  cancel
                </Icon>
              </div>
              <Text size="md">{description}</Text>
            </div>
            <Text className={styles.formLabel} weight="bold" noMargin>
              {`${t(keys.common.NOTES)}*`}
            </Text>
            <Form.Control
              type="text"
              as="textarea"
              required
              placeholder={t(keys.assessments.FAILURE_NOTES)}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <div className={styles.formSpacer} />

            {actionItemsCount >= 1 && (
              <div className={styles.assignedContainer}>
                <div className={styles.header}>
                  <TextWithIcon
                    leftIcon="report_problem"
                    weight="bold"
                    noMargin
                    color="orange"
                  >
                    Action Item(s) Assigned
                  </TextWithIcon>
                  <Icon
                    hover
                    style={{
                      color: showAssign ? 'black' : customColors.BLUE,
                      cursor: 'pointer',
                    }}
                    onClick={() => setShowAssign(!showAssign)}
                  >
                    {showAssign ? 'close' : 'add'}
                  </Icon>
                </div>
                <Spacer size="xs" />
                <div>
                  {actionItems?.map((a) => (
                    <div className={styles.assigned}>
                      <Label
                        name={t(statusStringLookup[a.status].title)}
                        color={statusStringLookup[a.status].color}
                      />
                      <div>
                        <SimpleUserCard
                          mediumAvatar
                          user={a.assignee}
                          disableClick
                        />
                      </div>
                      <Text truncate noMargin>
                        {a.description}
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Spacer />
            {online && !external && showAssign && (
              <>
                <Text className={styles.formLabel} weight="bold" noMargin>
                  {t(keys.assessments.ASSIGN_FAILURE_ACTION_ITEM)}
                </Text>
                <UserSelector
                  cardProps={{ largeAvatar: true }}
                  type="Assignee"
                  selected={assignee}
                  onSelectionChanged={(u) => setAssignee(u)}
                  onClose={() => {
                    setAssignee(() => setAssignee(undefined));
                  }}
                />
              </>
            )}
            <Spacer />
            <Text className={styles.formLabel} weight="bold" noMargin>
              {`${t(keys.common.REFERENCE_IMAGE)} (${t(keys.common.OPTIONAL)})`}
            </Text>
            {!!images.length && (
              <div className={styles.imageGallery}>
                {images.map((img, index) => (
                  <div key={`${img}-${index}`} className={styles.imageItem}>
                    <ImageWrapper
                      image={img}
                      className={styles.image}
                      alt="failure reference"
                    />
                    <div className={styles.deleteIconContainer}>
                      <Icon
                        className={styles.imgDelete}
                        baseClassName="material-icons-outlined"
                        onClick={() =>
                          setImages(images.filter((_, i) => i !== index))
                        }
                      >
                        delete
                      </Icon>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {loading ? (
              <Spinner
                className={styles.spinner}
                animation="border"
                variant="primary"
              />
            ) : (
              <MultiFileDropzone
                accept="image/*"
                onChange={handleFilesSelected}
              />
            )}
            <div className={styles.buttonContainer}>
              <Button
                size="md"
                variant="secondary"
                value={t(keys.action.CANCEL)}
                onClick={() => onHide()}
              />
              <Button
                size="md"
                variant="danger"
                disabled={!notes?.length || loading}
                value={t(keys.action.SUBMIT)}
                onClick={() => {
                  onSubmit(
                    notes,
                    images.length ? images.join('|') : undefined,
                    assignee?.id,
                  );
                  onHide();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
